import { useEffect, useMemo, useState } from 'react';
import { Plus, Trophy, TrendingUp, Scale, Ruler } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

function getWeekNumber() {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = (now - start + (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60000) / 86400000;
    return Math.ceil((diff + start.getDay() + 1) / 7);
}

export default function Progress() {
    const [measurements, setMeasurements] = useState([]);
    const [records, setRecords] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ week_number: getWeekNumber(), weight: '', waist: '', chest: '', arm: '', notes: '' });

    const load = async () => {
        setLoading(true);
        try {
            const [m, r, h] = await Promise.all([
                api.get('/measurements'),
                api.get('/records'),
                api.get('/workouts/history?limit=30'),
            ]);
            setMeasurements(m.data);
            setRecords(r.data);
            setHistory(h.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const body = {
            week_number: parseInt(form.week_number, 10),
            weight: form.weight ? parseFloat(form.weight) : null,
            waist: form.waist ? parseFloat(form.waist) : null,
            chest: form.chest ? parseFloat(form.chest) : null,
            arm: form.arm ? parseFloat(form.arm) : null,
            notes: form.notes,
        };
        if (!body.weight && !body.waist && !body.chest && !body.arm) {
            toast.error('Introduce al menos una medida');
            return;
        }
        await api.post('/measurements', body);
        toast.success('Medida guardada');
        setOpen(false);
        setForm({ week_number: getWeekNumber(), weight: '', waist: '', chest: '', arm: '', notes: '' });
        await load();
    };

    const chartData = useMemo(() => {
        // Build full 52 weeks, with gaps
        const byWeek = Object.fromEntries(measurements.map((m) => [m.week_number, m]));
        return Array.from({ length: 52 }, (_, i) => {
            const week = i + 1;
            const m = byWeek[week];
            return {
                week: `S${week}`,
                weight: m?.weight ?? null,
                waist: m?.waist ?? null,
                chest: m?.chest ?? null,
                arm: m?.arm ?? null,
            };
        });
    }, [measurements]);

    const latest = measurements[measurements.length - 1] || {};
    const first = measurements[0] || {};
    const weightDelta = (latest.weight && first.weight) ? (latest.weight - first.weight) : 0;

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1400px]">
            <header className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">Dashboard</div>
                    <h1 className="text-3xl md:text-4xl font-display font-semibold">Progreso</h1>
                </div>
                <Button onClick={() => setOpen(true)} data-testid="add-measurement-button"><Plus className="h-4 w-4 mr-2" /> Añadir medida</Button>
            </header>

            {/* Summary cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <Card className="bg-card border-border p-4" data-testid="summary-weight">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Scale className="h-3 w-3" /> Peso actual</div>
                    <div className="text-2xl font-display font-semibold tabular-nums">{latest.weight ? `${latest.weight.toFixed(2)} kg` : '—'}</div>
                    {weightDelta !== 0 && (
                        <div className={`text-xs mt-1 ${weightDelta < 0 ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--accent))]'}`}>
                            {weightDelta > 0 ? '+' : ''}{weightDelta.toFixed(2)} kg desde inicio
                        </div>
                    )}
                </Card>
                <Card className="bg-card border-border p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Ruler className="h-3 w-3" /> Cintura</div>
                    <div className="text-2xl font-display font-semibold tabular-nums">{latest.waist ? `${latest.waist} cm` : '—'}</div>
                </Card>
                <Card className="bg-card border-border p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Trophy className="h-3 w-3" /> Records</div>
                    <div className="text-2xl font-display font-semibold tabular-nums">{records.length}</div>
                </Card>
                <Card className="bg-card border-border p-4">
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Entrenos</div>
                    <div className="text-2xl font-display font-semibold tabular-nums">{history.filter((h) => h.finished_at).length}</div>
                </Card>
            </div>

            {/* Weight chart */}
            <Card className="bg-card border-border p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-widest">52 semanas</div>
                        <div className="text-lg font-display font-semibold">Evolución del peso</div>
                    </div>
                </div>
                {loading ? <Skeleton className="h-[280px] w-full" /> : (
                    <div className="h-[280px]" data-testid="weight-progress-line-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="hsl(220 10% 18%)" strokeDasharray="3 3" />
                                <XAxis dataKey="week" stroke="hsl(215 14% 70%)" fontSize={11} interval={3} />
                                <YAxis stroke="hsl(215 14% 70%)" fontSize={11} domain={['auto', 'auto']} />
                                <Tooltip contentStyle={{ background: 'hsl(220 14% 9%)', border: '1px solid hsl(220 10% 18%)', borderRadius: 10 }} labelStyle={{ color: 'hsl(215 14% 70%)' }} />
                                <Line type="monotone" dataKey="weight" stroke="hsl(142 72% 45%)" strokeWidth={2.5} dot={false} connectNulls activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </Card>

            {/* Records and measurements table */}
            <div className="grid lg:grid-cols-2 gap-3">
                <Card className="bg-card border-border p-5">
                    <div className="text-lg font-display font-semibold mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-[hsl(var(--primary))]" /> Records personales</div>
                    {records.length === 0 ? <div className="text-sm text-muted-foreground">Aún no hay records. ¡Empieza a entrenar!</div> : (
                        <div className="space-y-2">
                            {records.map((r) => (
                                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--surface-2))] border border-border" data-testid={`record-${r.id}`}>
                                    <div>
                                        <div className="text-sm font-medium">{r.exercise_name}</div>
                                        <div className="text-xs text-muted-foreground">{labelRecord(r.record_type)}</div>
                                    </div>
                                    <Badge className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">{formatRecordValue(r)}</Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
                <Card className="bg-card border-border p-5">
                    <div className="text-lg font-display font-semibold mb-3">Historial de medidas</div>
                    {measurements.length === 0 ? <div className="text-sm text-muted-foreground" data-testid="empty-weight-progress">Aún no hay medidas.</div> : (
                        <div className="space-y-1 max-h-[300px] overflow-auto scroll-soft">
                            {measurements.slice().reverse().map((m) => (
                                <div key={m.id} className="flex items-center justify-between py-2 border-b border-border text-sm">
                                    <div>
                                        <div className="font-medium">Semana {m.week_number}</div>
                                        <div className="text-xs text-muted-foreground">{new Date(m.date || m.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="text-right text-xs">
                                        {m.weight && <div>Peso: <span className="font-medium text-foreground">{m.weight.toFixed(2)} kg</span></div>}
                                        {m.waist && <div className="text-muted-foreground">Cintura: {m.waist} cm</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            {/* Add measurement dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Añadir medida semanal</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Semana (1-52)</Label><Input type="number" value={form.week_number} onChange={(e) => setForm({ ...form, week_number: e.target.value })} data-testid="measurement-week" /></div>
                            <div><Label>Peso (kg)</Label><Input inputMode="decimal" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="97.55" data-testid="measurement-weight" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div><Label>Cintura (cm)</Label><Input inputMode="decimal" value={form.waist} onChange={(e) => setForm({ ...form, waist: e.target.value })} data-testid="measurement-waist" /></div>
                            <div><Label>Pecho (cm)</Label><Input inputMode="decimal" value={form.chest} onChange={(e) => setForm({ ...form, chest: e.target.value })} data-testid="measurement-chest" /></div>
                            <div><Label>Brazo (cm)</Label><Input inputMode="decimal" value={form.arm} onChange={(e) => setForm({ ...form, arm: e.target.value })} data-testid="measurement-arm" /></div>
                        </div>
                        <div><Label>Notas</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="measurement-notes" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="save-measurement-button">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function labelRecord(t) {
    if (t === 'max_reps') return 'Máx. repeticiones';
    if (t === 'max_time_sec') return 'Máx. tiempo';
    if (t === 'max_weight_kg') return 'Máx. peso';
    return t;
}
function formatRecordValue(r) {
    if (r.record_type === 'max_reps') return `${r.value} reps`;
    if (r.record_type === 'max_time_sec') return `${r.value}s`;
    if (r.record_type === 'max_weight_kg') return `${r.value} kg`;
    return r.value;
}
